import React, { useState, useEffect, useCallback, useContext } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { logout, getUserLevel } from "../services/auth";
import { FaHome, FaChartBar, FaUsers, FaSignOutAlt, FaBars, FaTimes, FaCog } from "react-icons/fa";
import styles from '../styles/Header.module.css';
import { SettingsContext } from '../context/SettingsContext';
import logo from '../uploads/app_logos/app_logo.png';


const Header = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    // Desestruturação do contexto para pegar 'preferences' e 'isLoadingPreferences'
    const { preferences, isLoadingPreferences } = useContext(SettingsContext);

    // Estados locais para a cor do header e o logo (inicializados com valores padrão/seguros)
    // Eles serão atualizados via useEffect quando as preferências do contexto carregarem
    const [headerBgColor, setHeaderBgColor] = useState('#3498db'); // Cor padrão inicial
    //const [appLogoUrl, setAppLogoUrl] = useState('/imgs/logoHome.png'); // URL do logo padrão inicial
    const [appLogoUrl, setAppLogoUrl] = useState(logo); // URL do logo padrão inicial

    // Efeito para atualizar a cor do header e o logo quando as preferências do contexto carregarem ou mudarem
    useEffect(() => {
        if (!isLoadingPreferences && preferences) { // Garante que as preferências foram carregadas e existem
            setHeaderBgColor(preferences.headerColor);
            setAppLogoUrl(preferences.logoUrl);
        }
    }, [preferences, isLoadingPreferences]); // Depende de preferences e isLoadingPreferences

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
            if (window.innerWidth >= 768) {
                setMenuOpen(false);
            }
        };

        const handleScroll = () => {
            setScrolled(window.scrollY > 10);
        };

        window.addEventListener("resize", handleResize);
        window.addEventListener("scroll", handleScroll);

        return () => {
            window.removeEventListener("resize", handleResize);
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    const handleLogout = useCallback(() => {
        logout();
        navigate("/login");
    }, [navigate]);

    const toggleMenu = useCallback(() => {
        setMenuOpen((prev) => !prev);
    }, []);

    const userLevel = getUserLevel();

    const navItems = [
        { path: "/", label: "Home", icon: <FaHome /> },
        { path: "/relatorios", label: "Relatórios", icon: <FaChartBar /> },
        { path: "/usuarios", label: "Usuários", icon: <FaUsers /> },
        userLevel === 'admin' && { path: "/configuracoes", label: "Configurações", icon: <FaCog /> },
    ].filter(Boolean);

    // Se as preferências ainda estão carregando, podemos renderizar um header padrão
    // ou uma versão simplificada para evitar o erro.
    // Usaremos os estados locais inicializados com padrões até que o useEffect os atualize.
    if (isLoadingPreferences) {
        // Renderiza um header com cores e logo padrão enquanto carrega
        return (
            <header className={`${styles.header} ${scrolled ? styles.scrolled : ""}`} style={{ backgroundColor: '#3498db' }}>
                <div className={styles.container}>
                    <Link to="/" className={styles.logoLink}>
                        {/* Você pode usar um logo padrão hardcoded aqui ou um spinner */}
                        <img src="/imgs/logoHome.png" alt="Carregando Logo" className={styles.logo} />
                    </Link>
                    {/* Pode omitir a navegação até carregar ou mostrar um spinner */}
                    <span style={{ color: 'white', fontSize: '14px' }}>Carregando...</span>
                </div>
            </header>
        );
    }

    // Se as preferências já carregaram (ou falharam, mas preferem estar definidas por padrão), renderiza o header completo
    return (
        <header className={`${styles.header} ${scrolled ? styles.scrolled : ""}`} style={{ backgroundColor: headerBgColor }}>
            <div className={styles.container}>
                <Link to="/" className={styles.logoLink}>
                    <img src={appLogoUrl} alt="Logo Minhas Finanças" className={styles.logo} />
                </Link>

                {!isMobile && (
                    <nav className={styles.desktopNav}>
                        <ul className={styles.navList}>
                            {navItems.map((item) => (
                                <li key={item.path}>
                                    <Link
                                        to={item.path}
                                        className={`${styles.navLink} ${location.pathname === item.path ? styles.active : ""
                                            }`}
                                    >
                                        <span className={styles.icon}>{item.icon}</span>
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                            <li>
                                <button
                                    onClick={handleLogout}
                                    className={styles.logoutButton}
                                >
                                    <FaSignOutAlt className={styles.icon} />
                                    Sair
                                </button>
                            </li>
                        </ul>
                    </nav>
                )}

                {isMobile && (
                    <button
                        onClick={toggleMenu}
                        className={styles.menuButton}
                        aria-label={menuOpen ? "Fechar Menu" : "Abrir Menu"}
                    >
                        {menuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
                    </button>
                )}

                {isMobile && menuOpen && (
                    <div className={styles.mobileMenuOverlay}>
                        <ul className={styles.mobileNavList}>
                            {navItems.map((item) => (
                                <li key={item.path}>
                                    <Link
                                        to={item.path}
                                        onClick={toggleMenu}
                                        className={`${styles.mobileNavLink} ${location.pathname === item.path ? styles.active : ""
                                            }`}
                                    >
                                        <span className={styles.icon}>{item.icon}</span>
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                            <li>
                                <button
                                    onClick={handleLogout}
                                    className={styles.mobileLogoutButton}
                                >
                                    <FaSignOutAlt className={styles.icon} />
                                    Sair
                                </button>
                            </li>
                        </ul>
                    </div>
                )}
            </div>
        </header>
    );
};

export default Header;